import { keepPreviousData } from '@tanstack/react-query'
import { LoaderCircleIcon } from 'lucide-react'
import { useState } from 'react'

import CensusEmployeesTable from 'pages/employer_onboarding/setup/census/CensusEmployeesTable'
import { Banner, SolidCircleCheckIcon } from 'slab'
import { useGetQuotingProspectsProspectIdCensusCensusIdEmployees as useGetCensusEmployees } from 'utils/web_api'

import QuoteFlowButtonNav from '../QuoteFlowButtonNav'

type EmployeeConfirmationProps = {
  prospectId: string;
  censusId: string;
  isConfirming: boolean;
  onConfirm: () => void;
  onBack: () => void;
}

const PAGE_SIZE = 50

export default function EmployeeConfirmation ({
  prospectId,
  censusId,
  isConfirming,
  onConfirm,
  onBack,
}: EmployeeConfirmationProps) {
  const [pageIndex, setPageIndex] = useState(0)

  const { data: employees, isLoading, error } = useGetCensusEmployees(
    prospectId,
    censusId,
    {
      'page[size]': PAGE_SIZE,
      'page[number]': pageIndex + 1
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    }
  )

  if (isLoading) {
    return (
      <div className='flex flex-col items-center justify-center gap-2 h-full p-10'>
        <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
        <div className='text-sm text-gray-500'>Loading employees...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='p-10'>
        <Banner type='critical' title='Error loading employees'>
          {error instanceof Error && error.message}
        </Banner>
      </div>
    )
  }

  return (
    <div>
      <div className='bg-white rounded-2xl border border-gray-200 overflow-hidden mb-4 shadow'>
        <div className='bg-gray-50 p-4 rounded-t-2xl'>
          <div className='flex items-center gap-2'>
            <SolidCircleCheckIcon size={16} className='text-green-400' />
            <div>
              {employees?.pagination?.total_records} employee{employees?.pagination?.total_records === 1 ? '' : 's'} uploaded
            </div>
          </div>
        </div>
        <CensusEmployeesTable
          census={{
            quoting_prospect_id: prospectId,
            id: censusId,
          }}
          employees={employees?.data ?? []}
          totalEmployees={employees?.pagination?.total_records ?? 0}
          pageSize={PAGE_SIZE}
          pageIndex={pageIndex}
          onPageChange={setPageIndex}
        />
      </div>
      <QuoteFlowButtonNav
        onBack={onBack}
        onNext={onConfirm}
        isNextDisabled={isLoading}
        isLoading={isConfirming}
      />
    </div>
  )
}
